import { apiClient, apiRequestWithOfflineSupport } from './client';
import type { ApiResponse } from '../types/api';
import type { TarotReading, DrawnCard } from '../types/tarot';
import { TAROT_DECK } from '../data/tarotDeck';

export interface CreateReadingRequest {
  question: string;
  spreadId: string;
  cards: {
    cardId: string;
    cardName: string;  // Add cardName to the request
    position: number;
    isReversed: boolean;
  }[];
}

// Transform PWA format to API format
function transformReadingRequest(data: CreateReadingRequest) {
  console.log('[Readings API] transformReadingRequest input:', data);

  // Gemini spread keys — send as spreadKey (new flow)
  const GEMINI_SPREAD_KEYS = new Set(['daily_1', 'choice_2', 'timeline_3', 'celtic_10']);

  // Legacy spreadId → spreadType map (old flow)
  const legacySpreadTypeMap: Record<string, string> = {
    'single-card': 'single',
    'three-card': 'three_card',
    'celtic-cross': 'celtic_cross',
    'custom': 'custom',
  };

  // Transform cards to API format — keep string IDs (e.g. 'cups-5', 'major-0')
  // so they match the deep dive card scheme and getUsedCardIds() can exclude them correctly
  const cardsDrawn = data.cards.map((card) => ({
    cardId: card.cardId,
    cardName: card.cardName,
    position: card.position.toString(),
    reversed: card.isReversed
  }));

  // Decide which field to send
  const isGemini = GEMINI_SPREAD_KEYS.has(data.spreadId);
  const transformed = isGemini
    ? { question: data.question, spreadKey: data.spreadId, cardsDrawn }
    : { question: data.question, spreadType: legacySpreadTypeMap[data.spreadId] || 'single', cardsDrawn };

  console.log('[Readings API] transformReadingRequest output:', transformed);
  return transformed;
}

// Enrich backend cards with full card data from TAROT_DECK
function enrichCardsData(backendCards: any[]): DrawnCard[] {
  return backendCards.map((backendCard) => {
    // Find the full card data from TAROT_DECK
    // Backend card has: { cardId, cardName, position, reversed }
    // Try matching by name first (most reliable)
    let fullCard = TAROT_DECK.find(card =>
      backendCard.cardName && card.name.toLowerCase() === backendCard.cardName.toLowerCase()
    );

    // Fallback: match by cardId — the cardId is the numeric part of the TAROT_DECK id
    // e.g. 'cups-5' → cardId=5, 'swords-8' → cardId=8, 'major-0' → cardId=0
    // This is ambiguous (multiple suits share the same number) so we can't reliably
    // resolve without cardName. Show card back as fallback for old records.
    if (!fullCard) {
      console.warn(`Card not found in deck: cardName=${backendCard.cardName} cardId=${backendCard.cardId}`);
      return {
        id: 'unknown',
        name: backendCard.cardName || `Card ${backendCard.cardId}` || 'Unknown Card',
        arcana: 'major' as const,
        imagePath: '/assets/decorations/card-backs/rider-waite_back.webp',
        keywords: [],
        meanings: { upright: [], reversed: [] },
        description: '',
        position: parseInt(backendCard.position) || 1,
        isReversed: backendCard.reversed || false,
        positionMeaning: ''
      } as DrawnCard;
    }

    // Return enriched card with full data
    return {
      ...fullCard,
      position: parseInt(backendCard.position) || 1,
      isReversed: backendCard.reversed || false,
      positionMeaning: backendCard.positionMeaning || ''
    } as DrawnCard;
  });
}

export interface UpdateReadingRequest {
  interpretation?: string;
  aiInterpretation?: string;
  isFavorite?: boolean;
  isPrivate?: boolean;
  tags?: string[];
  status?: 'pending' | 'completed' | 'failed';
}

export interface ReadingStats {
  totalReadings: number;
  favoriteReadings: number;
  recentReadings: TarotReading[];
  readingsBySpread: { spreadId: string; count: number }[];
  readingsByStatus: { status: string; count: number }[];
}

export interface ReadingStatusResponse {
  id: string;
  status: 'pending' | 'completed' | 'failed';
  hasPrediction: boolean;  // Backend returns hasPrediction, not hasInterpretation
  createdAt: string;
  updatedAt: string;
  metadata?: any;
}

export const readingsAPI = {
  // Create new reading (with offline support and session monitoring)
  createReading: (data: CreateReadingRequest) => {
    const transformedData = transformReadingRequest(data);
    return apiRequestWithOfflineSupport<ApiResponse<TarotReading>>(
      '/api/readings',
      'POST',
      transformedData,
      { 
        queueable: true, 
        actionType: 'CREATE_READING',
        enableSessionMonitoring: true // Enable session monitoring during reading creation
      }
    );
  },
  
  // Get specific reading
  getReading: async (id: string) => {
    const response = await apiClient.get<any, ApiResponse<any>>(`/api/readings/${id}`);
    
    // Backend response structure: { success: true, data: { reading: {...} } }
    // We need to transform response.data.reading and then set it as response.data
    if (response.data && response.data.reading) {
      const reading = response.data.reading;
      
      // Enrich cards data with full card information from TAROT_DECK
      const enrichedCards = enrichCardsData(reading.cardsDrawn || reading.cards || []);
      
      // Transform and set as response.data (not response.data.reading)
      response.data = {
        ...reading,
        // Map backend field names to frontend field names
        spreadId: reading.readingType || reading.spreadId,
        cards: enrichedCards, // Use enriched cards with full data
        aiInterpretation: reading.aiPrediction || reading.aiInterpretation,
        // Keep other fields as-is
        id: reading.id,
        userId: reading.userId,
        question: reading.question,
        interpretation: reading.interpretation,
        isFavorite: reading.isFavorite || false,
        isPrivate: reading.isPrivate || false,
        status: reading.status || 'completed',
        tags: reading.tags || [],
        createdAt: reading.createdAt,
        updatedAt: reading.updatedAt
      };
    }
    
    return response as ApiResponse<TarotReading>;
  },
  
  // Get reading status (for polling during AI generation)
  getReadingStatus: (id: string) =>
    apiClient.get<any, ApiResponse<ReadingStatusResponse>>(`/api/readings/${id}/status`),
  
  // Update reading (with offline support)
  updateReading: (id: string, data: UpdateReadingRequest) => 
    apiRequestWithOfflineSupport<ApiResponse<TarotReading>>(
      `/api/readings/${id}`,
      'PUT',
      data,
      { queueable: true, actionType: 'UPDATE_READING' }
    ),
  
  // Delete reading (with offline support)
  deleteReading: (id: string) => 
    apiRequestWithOfflineSupport<ApiResponse<void>>(
      `/api/readings/${id}`,
      'DELETE',
      undefined,
      { queueable: true, actionType: 'DELETE_READING' }
    ),
  
  // Toggle favorite (with offline support)
  toggleFavorite: (id: string, isFavorite: boolean) =>
    apiRequestWithOfflineSupport<ApiResponse<TarotReading>>(
      `/api/readings/${id}`,
      'PATCH',
      { isFavorite },
      { queueable: true, actionType: 'FAVORITE_READING' }
    ),
  
  // Create deep dive reading
  createDeepDive: (data: { parentReadingId: string; question: string; cardCount: number }) =>
    apiClient.post<any, ApiResponse<any>>('/api/readings/deep-dive', data),

  // Get reading statistics
  getStats: () => 
    apiClient.get<any, ApiResponse<ReadingStats>>('/api/readings/stats'),
  
  // Get all readings (with transformation)
  getReadings: async (params?: { page?: number; limit?: number; favorite?: boolean }) => {
    console.log('[Readings API] getReadings called with params:', params); // Debug log
    
    // Use request method to ensure params are passed correctly
    // Note: apiClient response interceptor returns response.data, so response is the API response object
    const response: any = await apiClient.request({
      method: 'GET',
      url: '/api/readings',
      params: params
    });
    
    console.log('[Readings API] Response received:', {
      success: response.success,
      hasData: !!response.data,
      readingsCount: response.data?.readings?.length,
      pagination: response.data?.pagination
    }); // Debug log
    
    // Backend response structure: { success: true, data: { readings: [...], pagination: {...} } }
    if (response.data && response.data.readings) {
      // Transform each reading from backend format to frontend format
      const transformedReadings = response.data.readings.map((reading: any) => {
        // Enrich cards data with full card information from TAROT_DECK
        const enrichedCards = enrichCardsData(reading.cardsDrawn || []);
        
        return {
          id: reading.id,
          userId: reading.userId,
          question: reading.question || reading.title || 'Untitled Reading', // Fallback to title if question is missing
          spreadId: reading.readingType || 'single-card', // Map readingType to spreadId
          cards: enrichedCards, // Use enriched cards with full data
          interpretation: reading.interpretation,
          aiInterpretation: reading.aiPrediction || reading.aiInterpretation,
          isFavorite: reading.isFavorite || false,
          isPrivate: reading.isPrivate || false,
          status: reading.status || 'completed',
          tags: reading.tags || [],
          parentReadingId: reading.parentReadingId || null,
          deepDiveCount: reading.deepDiveCount || 0,
          createdAt: reading.createdAt,
          updatedAt: reading.updatedAt
        } as TarotReading;
      });
      
      // Return transformed data with proper ApiResponse structure
      return {
        success: response.success,
        data: {
          readings: transformedReadings,
          total: response.data.pagination?.total || transformedReadings.length,
          page: response.data.pagination?.page || 1,
          limit: response.data.pagination?.limit || 20
        }
      };
    }
    
    // Fallback if no readings
    return {
      success: response.success || true,
      data: {
        readings: [],
        total: 0,
        page: 1,
        limit: 20
      }
    };
  },
};